import {
  createContext,
  useEffect,
  useMemo,
  useState,
  type ComponentType,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";
import { FenceEditor } from "../viz/FenceEditor.js";
import type { VizEditorProps } from "../viz/registry.js";

/**
 * Lets an editor take over the modal's header — e.g. the SQL editor's drill-in
 * to a query file swaps the `label` for a back button + the path.
 * `setHead(null)` restores the default. "done" (close) is always kept.
 */
export interface ModalChrome {
  setHead: (node: ReactNode | null) => void;
}
export const ModalChromeContext = createContext<ModalChrome>({ setHead: () => {} });

/**
 * The embed edit modal, shared by both editing surfaces: the rendered page
 * (EmbedFrame, which splices the fence by byte range) and the rich editor
 * (editor/MilkdownEmbed, which rewrites a ProseMirror node). They disagree
 * about what "write this back" means and agree about everything else, so the
 * difference is exactly one prop: `updateCode`.
 */
export function EmbedModal({
  label,
  editor,
  attrs,
  source,
  updateCode,
  onClose,
}: {
  label: string;
  /** Custom editor for the kind; falls back to the raw fence editor. */
  editor?: ComponentType<VizEditorProps>;
  attrs: Record<string, string>;
  source: string;
  updateCode: (body: string, attrs?: Record<string, string>) => void;
  onClose: () => void;
}) {
  const Editor = editor ?? FenceEditor;
  // An editor may override the header. Reset on unmount so the next open
  // starts from the default.
  const [head, setHead] = useState<ReactNode | null>(null);
  const chrome = useMemo<ModalChrome>(() => ({ setHead }), []);

  // Escape closes, like every other modal surface.
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === "Escape") {
        e.preventDefault();
        onClose();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return createPortal(
    <div className="dw-modal-backdrop" onClick={onClose}>
      <div className="dw-modal" onClick={(e) => e.stopPropagation()}>
        <header className="dw-modal-head">
          {head ?? <span>{label}</span>}
          <button className="dw-link" data-testid="dw-embed-done" onClick={onClose}>
            done
          </button>
        </header>
        <ModalChromeContext.Provider value={chrome}>
          <Editor attrs={attrs} source={source} updateCode={updateCode} />
        </ModalChromeContext.Provider>
      </div>
    </div>,
    document.body,
  );
}
